import { createContext, useContext, useMemo } from 'react'

interface PermissionValue {
  has: (permission: string) => boolean
  /** Which role would grant it, for a refusal that names one. */
  grantedBy: (permission: string) => string[]
}

const PermissionContext = createContext<PermissionValue | null>(null)

export interface PermissionProviderProps {
  /** What this administrator holds, from `GET /v1/admin/me`. */
  permissions: string[]
  /** Every permission mapped to the roles that carry it, for the refusal wording. */
  roles?: Record<string, string[]>
  children: React.ReactNode
}

/**
 * What this administrator may do, put once at the root of the desk.
 *
 * A context rather than a prop threaded down, and rather than the components
 * reaching into the store themselves. Threading it means every screen and every
 * table row carries a `can` it does not use, which is the boilerplate the whole
 * structure exists to avoid; reaching into the store would tie the shared
 * components to Redux and make each one untestable on its own.
 */
export function PermissionProvider({ permissions, roles, children }: PermissionProviderProps) {
  const value = useMemo<PermissionValue>(() => {
    const held = new Set(permissions)
    return {
      has: (p) => held.has(p),
      grantedBy: (p) => roles?.[p] ?? [],
    }
  }, [permissions, roles])
  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>
}

/**
 * Deliberately throws when there is no provider above it.
 *
 * The alternative is returning "no" for everything, which hides the whole desk
 * and looks exactly like a correctly locked-down account. A blank screen that
 * might be a bug and might be the rules is the worst of both.
 */
export function usePermissions(): PermissionValue {
  const value = useContext(PermissionContext)
  if (!value) throw new Error('usePermissions needs a <PermissionProvider> above it')
  return value
}

/** `can('refund')`, for the many places that only need the answer. */
export function useCan(): (permission: string) => boolean {
  return usePermissions().has
}

/**
 * The same answer, or `null` when there is no provider — for the one caller that
 * has to work both ways.
 *
 * `AppShell` used to take its own `can` prop. That meant two sources for "may this
 * person do X": the rail could hide a section while a button inside it stayed live,
 * and nothing would ever say so. The prop is optional now and this is the fallback,
 * so there is one answer by default and an override only where a caller insists.
 */
export function useMaybeCan(): ((permission: string) => boolean) | null {
  return useContext(PermissionContext)?.has ?? null
}

export interface CanProps {
  /** One permission, or several of which any one is enough. */
  permission: string | string[]
  /**
   * `hide` removes it; `disable` leaves it visible and unpressable.
   *
   * Both are right in different places, which is why this does not pick for you.
   * 9.8 asks for both in one sentence: Support sees *"the same tables with fewer
   * rail items"* — the sections go, and the rows stay with their actions dead. A
   * Support administrator who cannot see that refunding exists cannot tell a
   * caller who to ask; one who can see the button and press it gets a refusal
   * from the server, which is worse.
   */
  mode?: 'hide' | 'disable'
  /** Drawn instead, when hidden. Usually nothing. */
  fallback?: React.ReactNode
  children: React.ReactNode
}

/**
 * Wraps anything only some administrators may use.
 *
 * A hundred and eight routes on the desk require a permission, and without this
 * each of the seventeen screens decides for itself how to check — which is
 * seventeen chances for one of them to show a Refund button to somebody who will
 * be refused by the server after pressing it.
 */
export function Can({ permission, mode = 'hide', fallback = null, children }: CanProps) {
  const { has, grantedBy } = usePermissions()
  const wanted = Array.isArray(permission) ? permission : [permission]
  const allowed = wanted.some(has)

  if (allowed) return <>{children}</>
  if (mode === 'hide') return <>{fallback}</>

  /**
   * Who can, on the thing that is greyed.
   *
   * Board 9.8: *"Actions you cannot take are greyed out. Select one to see
   * which role can take it."* The desk knew — `roles_with` maps every
   * permission to the roles holding it — and said "Your role cannot do this",
   * which is the half of the sentence somebody already knows.
   */
  const roles = [...new Set(wanted.flatMap(grantedBy))]

  const said =
    roles.length > 0
      ? `Your role cannot do this. ${roles.join(' or ')} can.`
      : 'Your role cannot do this, and no role currently has it.'

  return (
    /* The sentence sits on a wrapper that can still be pointed at. It was on
       the greyed element itself, which is `pointer-events-none` — so hovering
       showed nothing and the board's "select one to see which role can take
       it" was true for nobody using a mouse. Found walking the desk as Support
       on 2026-09-27. */
    <div title={said} className="cursor-not-allowed">
      <div
        // `inert` takes the whole subtree out of the tab order and out of reach of
        // a click, which is the only way to disable something that may contain
        // several controls. A pointer-events rule alone still lets Tab reach them.
        inert
        aria-disabled="true"
        className="pointer-events-none opacity-50"
      >
        {children}
      </div>
    </div>
  )
}
